<template>
  <v-card outlined rounded class="options-block px-3 py-2">
    <v-row v-if="!isEditMode" class="options-block__buttons">
      <template v-if="isType(['STANDARD'])">
        <v-col cols="12">
          <DownloadButton :shootingId="shootingId" :registration="registration" :date="date" />
        </v-col>
        <v-col cols="12" v-if="isNewFlow && hasLinkedModel">
          <ReprocessingButton :images="images" :shooting-id="shootingId" />
        </v-col>
        <v-col cols="12" v-if="!isNewFlow">
          <Restamp :images="images" />
        </v-col>
        <v-col cols="12" v-if="!isVirtualShooting && accessDMSOptions">
          <ReplayDMSButton />
        </v-col>
      </template>

      <template v-if="isType(['INSPECTION', 'INSPECTION_REPAIR_ORDER'])">
        <v-col cols="12">
          <DownloadReport
            :shootingId="shootingId"
            :registration="registration"
            :date="date"
            :shootingType="this.shootingType"
          />
        </v-col>
        <v-col cols="12" v-if="isType(['INSPECTION_REPAIR_ORDER'])">
          <!-- RO ADDENDUM -->
          <DownloadVhcReport :shootingId="shootingId" :registration="registration" :date="date" />
        </v-col>
      </template>

      <v-col
        cols="12"
        v-if="isType(['STANDARD', 'VIDEO', 'INSPECTION_REPAIR_ORDER']) && (accessEmailOptions || accessSmsOptions)"
      >
        <PlayerButtonDropdown :registration="registration" :shootingId="shootingId" :shootingType="shootingType" />
      </v-col>

      <v-col cols="12" v-if="isType(['STANDARD', 'VIDEO']) && !accessEmailOptions && !accessSmsOptions">
        <PlayerButton :shootingId="shootingId" />
      </v-col>

      <v-col cols="12" v-if="isType(['INSPECTION'])">
        <PlayerButton :shootingId="shootingId" />
      </v-col>

      <v-col cols="12" v-if="isType(['VIDEO']) && isCloudflareVideo">
        <DownloadVideoButton />
      </v-col>

      <template v-if="isType(['STANDARD']) && !details && !isNewFlow">
        <v-col cols="12" v-if="accessStampothequeLicensePlateCoverOptions">
          <EditingModeButton :text="$t('common.licensePlateCover')" type="LICENSE_PLATE_COVER" />
        </v-col>
        <v-col cols="12" v-if="accessStampothequeCroppingOptions">
          <EditingModeButton :text="$t('common.cropped')" type="CROPPING" />
        </v-col>
      </template>

      <v-col cols="12" v-if="isType(['STANDARD']) && isNewFlow && !hasLinkedModel">
        <v-alert type="info" text dense class="mb-0">
          {{ $t('reprocess.noModelMessage') }}
        </v-alert>
      </v-col>

      <v-col cols="12" v-if="isType(['STANDARD']) && accessDeleteOptions">
        <DeleteShooting :shootingId="shootingId" />
      </v-col>

      <v-col cols="12" v-if="isType(['VIDEO']) && accessDeleteOptions && isCloudflareVideo">
        <DeleteVideo :shootingId="shootingId" />
      </v-col>
    </v-row>

    <v-row v-else>
      <p class="body-1 text-center mt-3 px-2">{{ title }}</p>
      <div class="d-flex justify-center mb-3">
        <v-btn class="mx-2" small outlined @click="quitEditMode">{{ $t('common.cancel') }}</v-btn>
        <EditShootingButton :details="details" />
      </div>
    </v-row>
  </v-card>
</template>

<script>
import DownloadButton from '@/components/DownloadButton.vue'
import DeleteVideo from '@/components/shooting/buttons/DeleteVideo.vue'
import DownloadReport from '@/components/shooting/buttons/DownloadReport.vue'
import DownloadVhcReport from '@/components/shooting/buttons/DownloadVhcReport.vue'
import DownloadVideoButton from '@/components/shooting/buttons/DownloadVideoButton.vue'
import EditingModeButton from '@/components/shooting/buttons/EditingModeButton.vue'
import EditShootingButton from '@/components/shooting/buttons/EditShootingButton.vue'
import PlayerButton from '@/components/shooting/buttons/PlayerButton.vue'
import PlayerButtonDropdown from '@/components/shooting/buttons/PlayerButtonDropdown.vue'
import ReplayDMSButton from '@/components/shooting/buttons/ReplayDMSButton.vue'
import DeleteShooting from '@/components/shooting/DeleteShooting.vue'
import ReprocessingButton from '@/components/shooting/ReprocessingButton.vue'
import Restamp from '@/components/shooting/Restamp.vue'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'ShootingOptions',

  components: {
    DeleteShooting,
    DownloadButton,
    DownloadReport,
    DownloadVhcReport,
    DeleteVideo,
    DownloadVideoButton,
    EditingModeButton,
    EditShootingButton,
    PlayerButtonDropdown,
    PlayerButton,
    ReplayDMSButton,
    ReprocessingButton,
    Restamp
  },

  props: {
    date: String,
    images: Array,
    registration: String,
    shootingId: String,
    shootingType: String,
    details: Boolean
  },

  data() {
    return {
      editingMode: false
    }
  },

  computed: {
    ...mapGetters(['isVirtualShooting', 'hasSpecificAccess']),
    ...mapState({
      userOptions: (state) => state.userOptions,
      video: (state) => state.video,
      isEditMode: (state) => state.editShooting.isEditMode,
      editionTypeSelected: (state) => state.editShooting.editionTypeSelected,
      userDetails: (state) => state.usersConfig.userDetails,
      shootingModel: (state) => state.shootingModel
    }),
    isNewFlow() {
      return this.userDetails && this.userDetails.newFlow === true
    },
    hasLinkedModel() {
      return this.shootingModel !== null && this.shootingModel !== undefined
    },
    title() {
      let convertedTypeToText

      const croppedText = this.$t('common.cropped')
      const licenseText = this.$t('common.licensePlateCover')
      const space = ' '

      this.editionTypeSelected === 'CROPPING'
        ? (convertedTypeToText = croppedText)
        : (convertedTypeToText = licenseText)

      return this.$t('common.plzSelectPhotos') + space + convertedTypeToText
    },
    isCloudflareVideo() {
      return (this.video && this.video.streamer === 'CLOUDFLARE') || (this.video && this.video.streamer === 'YOUTUBE')
    },
    accessEmailOptions() {
      return this.hasSpecificAccess('useStampothequeEmail')
    },
    accessSmsOptions() {
      return this.hasSpecificAccess('useStampothequeSMS')
    },
    accessDeleteOptions() {
      return this.hasSpecificAccess('useDeleteShooting')
    },
    accessDMSOptions() {
      return this.hasSpecificAccess('useResendDMSOnStampotheque')
    },
    accessStampothequeLicensePlateCoverOptions() {
      return this.hasSpecificAccess('stampothequeLicensePlateCover')
    },
    accessStampothequeCroppingOptions() {
      return this.hasSpecificAccess('stampothequeCropping')
    }
  },

  methods: {
    isType(types) {
      for (let i = 0; i < types.length; i++) {
        if (types[i] === this.shootingType) return true
      }
      return false
    },
    quitEditMode() {
      this.$store.commit('UPDATE_SHOOTING_EDITION_MODE', {
        editionTypeSelected: null,
        isEditMode: false,
        selectedImages: []
      })
    }
  }
}
</script>

<style lang="sass" scoped>
.options-block
  min-height: 60px
  max-width: 100%
  min-width: 208px
</style>
